import { z } from "zod";

import { i18n } from "@libs/i18n";
import {
  publicEnrolmentStatusEnumSchema,
  publicRegistrationStatusEnumSchema,
} from "@libs/schemas";
import { EnrolmentType } from "@/domain/enrolment";
import {
  computeEnrolmentInvoiceSummary,
  InvoiceForEnrolmentSummaryType,
} from "@/utils/invoices";

export type EnrolmentStatusType = z.infer<
  typeof publicEnrolmentStatusEnumSchema
>;

export type EnrolmentRegistrastionStatusType = z.infer<
  typeof publicRegistrationStatusEnumSchema
>;

export const formatEnrolmentStatus = (status: EnrolmentStatusType): string => {
  switch (status) {
    case "PENDING":
      return i18n.t("main:registrations.enrolmentStatus.pending");
    case "ACTIVE":
      return i18n.t("main:registrations.enrolmentStatus.active");
    case "COMPLETED":
      return i18n.t("main:registrations.enrolmentStatus.completed");
    case "CANCELLED":
      return i18n.t("main:registrations.enrolmentStatus.cancelled");
    case "UNCOMPLETED":
      return i18n.t("main:registrations.enrolmentStatus.uncompleted");
    case "TO_CERTIFY":
      return i18n.t("main:registrations.enrolmentStatus.toCertify");
    default:
      return status;
  }
};

export const formatEnrolmentRegistrationStatus = (
  status: EnrolmentRegistrastionStatusType,
): string => {
  switch (status) {
    case "CONTRACT_TO_SEND":
      return i18n.t("main:registrations.registrationStatus.contractToSend");
    case "DOCS_DENIED":
      return i18n.t("main:registrations.registrationStatus.docsDenied");
    case "DOCS_REQUIRED":
      return i18n.t("main:registrations.registrationStatus.docsRequired");
    case "DOCS_TO_REVIEW":
      return i18n.t("main:registrations.registrationStatus.docsToReview");
    case "SIGNED":
      return i18n.t("main:registrations.registrationStatus.signed");
    case "STUDENT_TO_SIGN":
      return i18n.t("main:registrations.registrationStatus.studentToSign");
    default:
      return status;
  }
};

export const getEnrolmentStatusBadgeVariant = (
  status: EnrolmentStatusType,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "PENDING":
      return "outline";
    case "ACTIVE":
      return "default";
    case "COMPLETED":
      return "secondary";
    case "CANCELLED":
      return "destructive";
    default:
      return "outline";
  }
};

export const getEnrolmentRegistrationStatusBadgeVariant = (
  status: EnrolmentRegistrastionStatusType,
): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "DOCS_REQUIRED":
    case "STUDENT_TO_SIGN":
      return "default";
    case "DOCS_TO_REVIEW":
    case "CONTRACT_TO_SEND":
      return "secondary";
    case "DOCS_DENIED":
      return "destructive";
    case "SIGNED":
      return "outline";
    default:
      return "default";
  }
};

export const countMissingContractFields = (
  enrolment: EnrolmentType,
): number => {
  let count = 0;

  if (!enrolment.student.date_of_birth) count++;

  if (
    !enrolment.street_address ||
    !enrolment.city ||
    !enrolment.province ||
    !enrolment.postal_code
  ) {
    count++;
  }

  if (!enrolment.driver_licence_number) count++;
  if (!enrolment.driver_licence_class) count++;
  if (!enrolment.driver_licence_expiry) count++;

  return count;
};

export const normalizeEnrolmentListItem = <
  T extends {
    status?: string | null;
    enrolments_products: { price: number | null }[] | null;
    invoices: InvoiceForEnrolmentSummaryType[];
  },
>(
  rawData: T,
) => {
  const { enrolments_products, invoices, ...params } = rawData;

  const productsTotal =
    enrolments_products?.reduce(
      (sum, product) => sum + (product.price ?? 0),
      0,
    ) ?? 0;

  const summary = computeEnrolmentInvoiceSummary({
    invoices,
    productsTotal,
    enrolmentStatus: params.status,
  });

  return {
    ...params,
    ...summary,
  };
};
